/**
 * Phase 1 TRACK A — dev-only debug overlay.
 *
 * Renders the structured `phase1_trace` block emitted by the backend
 * (lives under `retrieval_debug.phase1_trace` on each assistant message
 * when RETRIEVAL_DEBUG_QUERY=true on the server). The shape is locked
 * down by `backend/tests/test_phase1_trace.py`; later tracks fill in the
 * null fields without changing keys.
 *
 * Visibility:
 *   1. Provide `enabled` prop (parent decides via `?debug=1` query param), OR
 *   2. Set `localStorage.setItem('phase1_debug', '1')` and reload.
 *
 * Renders inline below the bot message. Collapsible.
 */
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

const STORAGE_FLAG = 'phase1_debug';

export const isDebugEnabledFromEnv = () => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.localStorage?.getItem(STORAGE_FLAG) === '1') return true;
    const params = new URLSearchParams(window.location?.search || '');
    return params.get('debug') === '1';
  } catch (e) {
    return false;
  }
};

const formatMs = (value) => (value == null ? '—' : `${value} ms`);
const formatBool = (value) => (value == null ? '—' : value ? 'yes' : 'no');
const formatStr = (value) => (value == null || value === '' ? '—' : String(value));
const formatList = (value) => {
  if (!Array.isArray(value) || value.length === 0) return '—';
  return value.join(', ');
};

const Section = ({ title, children }) => (
  <SectionContainer>
    <SectionTitle>{title}</SectionTitle>
    <SectionBody>{children}</SectionBody>
  </SectionContainer>
);

const Row = ({ label, value }) => (
  <RowContainer>
    <RowLabel>{label}</RowLabel>
    <RowValue>{value}</RowValue>
  </RowContainer>
);

const DebugOverlay = ({ trace, enabled }) => {
  const [collapsed, setCollapsed] = useState(true);
  const phase1 = useMemo(() => trace?.phase1_trace || null, [trace]);

  if (!enabled || !phase1) return null;

  return (
    <Container>
      <Header type="button" onClick={() => setCollapsed((c) => !c)}>
        <span>phase1_trace ({phase1.schema_version || 'unknown'})</span>
        <span>{collapsed ? '▸' : '▾'}  total {formatMs(phase1.total_ms)}</span>
      </Header>
      {!collapsed && (
        <Body>
          <Section title="NLU">
            <Row label="intent" value={formatStr(phase1.nlu?.intent)} />
            <Row label="merge_action" value={formatStr(phase1.nlu?.merge_action)} />
            <Row label="topic_shift" value={formatStr(phase1.nlu?.topic_shift)} />
            <Row label="target_product" value={formatStr(phase1.nlu?.target_product)} />
            <Row label="asked_attribute" value={formatStr(phase1.nlu?.asked_attribute)} />
            <Row label="pronoun→active" value={formatBool(phase1.nlu?.pronoun_to_active_product)} />
            <Row label="tool" value={formatStr(phase1.nlu?.tool)} />
            <Row label="latency" value={formatMs(phase1.nlu?.latency_ms)} />
          </Section>

          <Section title="Merge">
            <Row label="action" value={formatStr(phase1.merge?.merge_action)} />
            <Row label="ops_applied" value={formatList(phase1.merge?.ops_applied)} />
            <Row
              label="active_product"
              value={`${formatStr(phase1.merge?.active_product_before?.name)} → ${formatStr(
                phase1.merge?.active_product_after?.name,
              )}`}
            />
          </Section>

          <Section title="Retrieval">
            <Row label="skipped" value={formatBool(phase1.retrieval?.skipped)} />
            <Row label="path" value={formatStr(phase1.retrieval?.response_path)} />
            <Row label="tier" value={formatStr(phase1.retrieval?.tier)} />
            <Row label="match_mode" value={formatStr(phase1.retrieval?.match_mode)} />
            <Row label="hybrid" value={formatBool(phase1.retrieval?.hybrid)} />
            <Row label="buckets" value={formatList(phase1.retrieval?.preferred_buckets)} />
            <Row label="n_results" value={formatStr(phase1.retrieval?.n_results)} />
            <Row label="n_after_filter" value={formatStr(phase1.retrieval?.n_after_filter)} />
            <Row label="qdrant_ms" value={formatMs(phase1.retrieval?.qdrant_ms)} />
          </Section>

          <Section title="Response">
            <Row label="template" value={formatStr(phase1.response?.template)} />
            <Row label="model" value={formatStr(phase1.response?.model)} />
            <Row label="input_tokens" value={formatStr(phase1.response?.input_tokens)} />
            <Row label="cached_tokens" value={formatStr(phase1.response?.cached_tokens)} />
            <Row label="output_tokens" value={formatStr(phase1.response?.output_tokens)} />
            <Row label="latency" value={formatMs(phase1.response?.latency_ms)} />
          </Section>

          <DetailsBlock>
            <summary>raw JSON</summary>
            <pre>{JSON.stringify(phase1, null, 2)}</pre>
          </DetailsBlock>
        </Body>
      )}
    </Container>
  );
};

export default DebugOverlay;

// --- styled ---

const Container = styled.div`
  margin-top: 6px;
  border: 1px dashed #9aa0a6;
  border-radius: 4px;
  background: #f7f7f7;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 11px;
  color: #1a1a1a;
`;

const Header = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ececec;
  border: 0;
  border-bottom: 1px dashed #9aa0a6;
  padding: 6px 8px;
  cursor: pointer;
  font: inherit;
  text-align: left;
`;

const Body = styled.div`
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SectionContainer = styled.div`
  border-top: 1px dotted #ccc;
  padding-top: 4px;
  &:first-child {
    border-top: 0;
    padding-top: 0;
  }
`;

const SectionTitle = styled.div`
  font-weight: 600;
  color: #444;
  margin-bottom: 2px;
`;

const SectionBody = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 2px 8px;
`;

const RowContainer = styled.div`
  display: contents;
`;

const RowLabel = styled.div`
  color: #5f6368;
`;

const RowValue = styled.div`
  word-break: break-word;
`;

const DetailsBlock = styled.details`
  margin-top: 4px;
  pre {
    margin: 4px 0 0;
    padding: 4px;
    background: #fff;
    border: 1px solid #e0e0e0;
    overflow: auto;
    max-height: 200px;
  }
`;
